import { Box, Chip, Divider, Modal, ModalClose, ModalDialog, Stack } from '@mui/joy';
import type { ColumnState } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import GAgGrid, { type GAgGridProps } from 'components/technical/grids/GAgGrid';
import { cloneDeep, pull } from 'lodash/fp';
import { type ReactElement, type RefObject, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { REPLAY_UNMASK_CLASS_NAME } from 'setup/initializeAll.ts';
import { defaultRowSpacing } from 'components/StackSpacing';
import { useDefaultErrorHandling } from 'components/technical/UseDefaultErrorHandling';
import { useUserSettingsQuery, useUpdateUserSettingsMutation, UserSettingsDocument } from 'generated/graphql';

import type { ReportPreset, ReportPresetSettings, ReportSettings } from './GAgGridPresets.types';
import ReportPresets from './ReportPresets';
import { useFeedback } from '../Feedback/UseFeedback.tsx';
import Loader from '../Loader/Loader';
import { validatePresetColState } from './validateColId.ts';
// import { logOnce } from 'components/log.utils.ts';
import { Check, Warning } from '@mui/icons-material';
import ColsHandler from './ColsHanlder';
import { useAuth } from 'components/management/NewAuthProvider';

type GAgGridPresetsProps<RowData> = GAgGridProps<RowData> & {
  presetSettingsKey: string;
  defaultPresets: ReportPreset[];
};

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
};

/** wrapper around GAgGrid, show and manages user saved presets */
function GAgGridPresets<RowData>(props: GAgGridPresetsProps<RowData>): ReactElement {
  const { isAdmin } = useAuth();
  const [missingColIds, setMissingColIds] = useState<string[]>([]);
  const [definedColIds, setDefinedColIds] = useState<string[]>([]);
  const [showColsHandler, setShowColsHandler] = useState(false);
  const { defaultPresets, presetSettingsKey, ...rest } = props;
  const [updateUserSettings, { loading }] = useUpdateUserSettingsMutation();
  const userSettingsQueryResult = useDefaultErrorHandling(
    useUserSettingsQuery({
      variables: {
        field: presetSettingsKey,
      },
    }),
    { disableNoDataFallback: true }
  );
  const [presetApplied, setPresetApplied] = useState(false);
  const localRef = useRef<AgGridReact>(null);
  const ref = (props.ref as RefObject<AgGridReact<RowData>>) ?? localRef;

  const { showSuccessMessage } = useFeedback();
  const currentPresetNameKey = `${presetSettingsKey}-currentPresetName`;
  // use localStorage to save current preset and load on initial render
  // we intentionally don't want to listen to other tabs changes, it would create issues with impersonation (2 tabs with different users)

  function getGridRef(): AgGridReact<RowData> | null {
    return ref.current;
  }

  const [currentPresetName, setCurrentPresetName] = useLocalStorage(currentPresetNameKey, defaultPresets[0].name);

  if (props.ref && typeof props.ref !== 'object') {
    throw new Error('GAgGridPresets component expects a RefObject as a ref.');
  }
  if (defaultPresets.length === 0) {
    throw new Error('GAgGridPresets component expects at least one default preset.');
  }

  if (!userSettingsQueryResult.loaded) {
    return <userSettingsQueryResult.Fallback />;
  }

  const userReportSettings: ReportSettings = userSettingsQueryResult.data.management.userSettings ?? {
    presets: [],
    favorites: defaultPresets.map((preset: ReportPreset) => preset.name), // initially default presets are in favorites
  };

  const allPresets = [...(userReportSettings.presets ?? []), ...defaultPresets];
  const currentPreset = allPresets.find((preset) => preset.name === currentPresetName);

  if (currentPresetName && !currentPreset) {
    // happens on preset creation, firstly on rerender we have new currentPresetName, but it appears in allPresets only after refetch
    console.info(`currentPreset ${currentPresetName} was not found, fallback to the first default preset`);
  }

  const currentPresetOrDefault = currentPreset ?? defaultPresets[0];

  function getCurrentReportState(): ReportPresetSettings {
    const gridRef = getGridRef();

    return {
      filters: gridRef?.api?.getFilterModel() ?? {},
      pivotMode: gridRef?.api?.isPivotMode() ?? false,
      columns: gridRef?.api?.getColumnState() ?? [],
    };
  }

  function applyPresetToReport(preset: ReportPreset): void {
    const gridRef = getGridRef();
    if (!gridRef?.api || !gridRef?.api?.getColumns()) {
      console.log('no gridRef?.api or no gridRef?.api?.getColumns()');
      return;
    }

    const currentColIds = (gridRef?.api?.getColumns() ?? []).map((col) => col.getColId()).filter(Boolean);
    setDefinedColIds(currentColIds);

    gridRef.api.setFilterModel(preset.settings.filters);
    gridRef.api.setGridOption('pivotMode', preset.settings.pivotMode);

    const state = preset.settings.columns.map((column: ColumnState) => ({
      ...column,
      width: undefined,
    }));

    const validationErrors = validatePresetColState(preset.settings.columns, currentColIds);
    setMissingColIds(validationErrors);

    const columnStateAppliedSuccessfully = gridRef.api.applyColumnState({
      state,
      applyOrder: false,
    });

    if (!columnStateAppliedSuccessfully && isAdmin) {
      console.warn('Applying report preset error: some saved columns was not found in grid column configuration.');
    }

    setTimeout(() => {
      gridRef.api.autoSizeAllColumns();
      setTimeout(() => setPresetApplied(true), 1);
    }, 1);
  }

  async function mutateAndUpdateSettings(mutate: (settings: ReportSettings) => void): Promise<void> {
    const newReportSettings = cloneDeep<ReportSettings>(userReportSettings);
    mutate(newReportSettings);

    await updateUserSettings({
      variables: {
        settings: {
          [presetSettingsKey]: newReportSettings,
        },
      },
      refetchQueries: [UserSettingsDocument],
    });
  }

  return (
    <Stack height="100%" gap={defaultRowSpacing} className={REPLAY_UNMASK_CLASS_NAME}>
      <ReportPresets
        loading={loading}
        gridColIds={definedColIds}
        userSettings={userReportSettings}
        defaultPresets={defaultPresets}
        currentPreset={currentPresetOrDefault}
        setCurrentPreset={(preset): void => {
          setCurrentPresetName(preset.name);
          const gridRef = getGridRef();
          if (gridRef?.api) {
            applyPresetToReport(preset);
          }
        }}
        onAddPreset={async ({ name, isFavorite }): Promise<void> => {
          if (!name || name === '') {
            throw new Error('Preset name is required');
          }
          // const issues = validatePresetColState(currentPresetOrDefault.settings.columns, definedColIds);
          // if (issues.length && isAdmin) {
          //   for (const msg of issues) {
          //     logOnce(msg, 'warn');
          //   }
          //   return;
          // }

          await mutateAndUpdateSettings((newSettings) => {
            newSettings.presets.push({
              name,
              settings: getCurrentReportState(),
              isDefault: false,
            });
            if (isFavorite) {
              newSettings.favorites.push(name);
            }
          });
          setCurrentPresetName(name);
        }}
        onSavePreset={(): Promise<void> =>
          mutateAndUpdateSettings((newSettings) => {
            newSettings.presets.find((preset) => preset.name === currentPresetOrDefault.name)!.settings =
              getCurrentReportState();
          })
        }
        onDeletePreset={async (presetToDelete): Promise<void> => {
          mutateAndUpdateSettings((newSettings) => {
            newSettings.presets = newSettings.presets.filter((preset) => preset.name !== presetToDelete.name);
            newSettings.favorites = pull(presetToDelete.name, newSettings.favorites);
            if (currentPresetName === presetToDelete.name) {
              setCurrentPresetName(newSettings.favorites[0] ?? defaultPresets[0].name);
            }
          });
        }}
        setFavourites={async (value: string[]): Promise<void> => {
          mutateAndUpdateSettings((newSettings) => {
            newSettings.favorites = value;
          });
        }}
        onResetPreset={(): void => applyPresetToReport(currentPresetOrDefault)}
        copyCurrentStateAsJson={(): void => {
          const stateToPresetJson = JSON.stringify(
            {
              name: '[PUT UNIQUE PRESET NAME HERE]',
              isDefault: true,
              settings: getCurrentReportState(),
            } satisfies ReportPreset,
            null,
            2
          );
          navigator.clipboard.writeText(stateToPresetJson);
          showSuccessMessage(
            'Current report state was copied to clipboard as JSON, replace name field with unique new preset name and it can be used as a new default preset in code'
          );
        }}
      />
      {!presetApplied && <Loader appearance="container" />}
      <Stack sx={{ visibility: presetApplied ? 'visible' : 'hidden' }} height="100%" direction="column">
        {isAdmin && (
          <Stack direction="row" justifyContent="flex-start" alignItems="center" sx={{ py: 1 }}>
            {missingColIds.length > 0 ? (
              <Box onClick={() => setShowColsHandler(true)} sx={{ cursor: 'pointer' }}>
                <Chip size="sm" color="danger" startDecorator={<Warning fontSize="sm" sx={{ mr: 0.5 }} />}>
                  {missingColIds.length} preset columns missing in grid
                </Chip>
              </Box>
            ) : (
              definedColIds.length > 0 &&
              missingColIds.length === 0 && (
                <Chip size="sm" color="success" disabled startDecorator={<Check fontSize="sm" sx={{ mr: 0.5 }} />}>
                  All preset columns exist in grid
                </Chip>
              )
            )}
          </Stack>
        )}
        <GAgGrid<RowData>
          ref={ref}
          defaultColDef={defaultColDef}
          {...rest}
          onGridReady={(event): void => {
            applyPresetToReport(currentPresetOrDefault);
            props.onGridReady?.(event);
          }}
        />
      </Stack>
      {isAdmin && (
        <Modal open={showColsHandler} onClose={() => setShowColsHandler(false)}>
          <ModalDialog variant="outlined">
            <ModalClose />
            <Stack mb={2}>
              <Box>
                The{' '}
                <Chip color="danger" variant="outlined">
                  {currentPresetOrDefault.name}
                </Chip>{' '}
                preset has{' '}
                <Chip color="danger" variant="outlined">
                  {missingColIds.length} columns
                </Chip>{' '}
                missing in grid
              </Box>
            </Stack>
            <Divider />
            <ColsHandler
              isDefault={currentPresetOrDefault.isDefault}
              gridColIds={definedColIds ?? []}
              cols={missingColIds}
              onReplace={async (oldColId, newColId) => {
                const newSettings = cloneDeep(userReportSettings);

                const presetIndex = newSettings.presets.findIndex((p) => p.name === currentPresetOrDefault.name);
                if (presetIndex !== -1) {
                  const preset = newSettings.presets[presetIndex];
                  preset.settings.columns = preset.settings.columns.map((c) =>
                    c.colId === oldColId ? { ...c, colId: newColId } : c
                  );
                  newSettings.presets[presetIndex] = preset;
                }

                await updateUserSettings({
                  variables: {
                    settings: { [presetSettingsKey]: newSettings },
                  },
                  refetchQueries: [UserSettingsDocument],
                });

                setMissingColIds((prev) => prev.filter((id) => id !== oldColId));

                const updatedPreset = newSettings.presets.find((p) => p.name === currentPresetOrDefault.name);
                if (updatedPreset) {
                  applyPresetToReport(updatedPreset);
                }
              }}
              onDelete={async (colIdToDelete) => {
                const newSettings = cloneDeep(userReportSettings);

                const preset = newSettings.presets.find((p) => p.name === currentPresetOrDefault.name);
                if (preset) {
                  preset.settings.columns = preset.settings.columns.filter((c) => c.colId !== colIdToDelete);
                }

                await updateUserSettings({
                  variables: {
                    settings: { [presetSettingsKey]: newSettings },
                  },
                  refetchQueries: [UserSettingsDocument],
                });

                setMissingColIds((prev) => prev.filter((id) => id !== colIdToDelete));

                const updatedPreset = newSettings.presets.find((p) => p.name === currentPresetOrDefault.name);
                if (updatedPreset) {
                  applyPresetToReport(updatedPreset);
                }
              }}
            />
          </ModalDialog>
        </Modal>
      )}
    </Stack>
  );
}

export default GAgGridPresets;
